import { head } from 'lodash'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '@/hooks/store'
import { GroupsWithQuantity } from '@/models/service'
import { cartSlice } from '@/store/cartSlice'
import { dayDifferenceSelector } from '@/store/insertPeriodSlice'
import { licenseSlice } from '@/store/licenseSlice'
import { formatPrice } from '@/utils/price'
import {
  ItemGroup,
  getGroupLabel,
  getItemGroups,
} from '@/utils/timeSchedule/groupItems'
import { adjustEndTime, isFullDay } from '@/utils/timeSchedules'

interface Props {
  service: GroupsWithQuantity[number]['services'][number]
}

export function SidebarServiceMobile(props: Props) {
  const { i18n } = useTranslation()

  const cartReservations = useAppSelector(cartSlice.selectors.cartReservations)
  const dayDifference = useAppSelector(dayDifferenceSelector)
  const license = useAppSelector(licenseSlice.selectors.license)

  const itemGroups = useMemo(
    () =>
      getItemGroups(
        cartReservations,
        { spotName: null, spotType: null },
        props.service.serviceId
      ),
    [cartReservations, props.service]
  )

  if (props.service.scheduleType === 'daily') {
    return itemGroups.map((group, idx) => {
      const label = getGroupLabel({
        dayDifference,
        group,
        license,
        locale: i18n.language,
      })

      return (
        <div className="flex items-center justify-between text-sm" key={idx}>
          <div className="flex items-center space-x-2">
            <img
              alt={props.service.name}
              className="size-4"
              src={`/icons/${props.service.icon}`}
            />

            <div>
              {props.service.quantity} x {props.service.name}
              <div>{label}</div>
            </div>
          </div>

          <div className="font-medium text-secondary">
            {formatPrice(group.items[0].price * group.items[0].quantity)}
          </div>
        </div>
      )
    })
  }

  function getServiceName(group: ItemGroup) {
    const firstItem = head(group.items)
    const suffix = firstItem?.timeScheduleName
      ? ` · ${firstItem.timeScheduleName}`
      : ''
    return props.service.name + suffix
  }

  return itemGroups.map((group, idx) => {
    const label = getGroupLabel({
      dayDifference,
      group,
      license,
      locale: i18n.language,
    })

    return (
      <div className="flex items-center justify-between text-sm" key={idx}>
        <div className="flex items-center space-x-2">
          <img
            alt={props.service.name}
            className="size-4"
            src={`/icons/${props.service.icon}`}
          />

          <div>
            {group.quantity} x {getServiceName(group)}
            {isFullDay(group)
              ? null
              : ` · ${group.startTime}-${adjustEndTime(group.endTime)}`}
            <div>{label}</div>
          </div>
        </div>

        <div className="font-medium text-secondary">
          {formatPrice(group.items[0].price * group.items[0].quantity)}
        </div>
      </div>
    )
  })
}
